import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import { Text, Button, Icons, Spinner } from "@evvve/ui-kit";
import Link from "src/shared-features/Link";
import styles from "./style.module.scss";
import { IconProps } from "@evvve/ui-kit/dist/components/Icon/IconWrap";
import { formattedUserSiteURL } from "./service";
import { useDesktopType } from "src/hooks";
import { ReactComponent as MessageOff } from "src/assets/images/message_off.svg";
import CensoredProfileInfo from "./features/CensoredProfileInfo";
import { useGetUserQuery, useLazyFollowUserQuery, useLazyUnfollowUserQuery } from "src/store/User/api";
import { DEFAULT_NOTIFICATION_ESTIMATE, SocialMedia } from "src/constants";
import avatar_skeleton from "src/assets/images/avatar_skeleton.png";
import { CurrentChatPage, DialogLastMessage, Dialog as DialogT, DialogType, MessageType } from "../types";
import Dialog from "../Dialog";
import { useLazyGetDialogByUserIdQuery } from "src/store/chat/api";
import { addToast } from "src/store/toast/slice";
import { GetUserResponse } from "src/store/User/types";
import { ChatSocketIoContext } from "src/shared-features/Wrappers";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/hooks/useStoreHooks";
import { closeChat } from "src/store/chat/chatSlice";
import { decrementFollowingCount, incrementFollowingCount } from "src/store/User/slice";
import { refetchUser } from "src/store/otherUser/otherUserSlice";
import { isOtherUserProfileEnabled } from "src/helpers/featureFlags";

const {
  Facebook: FacebookIcon,
  Instagram: InstagramIcon,
  Twitter: TwitterIcon,
  Linkedin: LinkedinIcon,
} = Icons;

interface Props {
  memberId: string;
  onClose?: (() => void);
}

type UpdateUserMsg = { user_id: string; name: string; avatar_link: string };

const ViewProfile = ({ memberId, onClose }: Props) => {
  const chatSocket = useContext(ChatSocketIoContext);

  const userToRefetch = useAppSelector(state => state.otherUser.userToRefetch);

  const navigate = useNavigate();

  // const followersAmountTextSize: typeSizesProduct = ["s", "s", "s", "s", "s", 6];
  // const followersTextSize: typeSizesProduct = ["xs", "xs", "xs", "xs", "xs", "s"];
  const isDesktop = useDesktopType();
  const dispatch = useAppDispatch();

  // const { currentData: userInfo, isLoading, refetch } = useGetUserQuery({ userId: memberId }, { skip: !memberId });
  const getUserState = useGetUserQuery({ userId: memberId }, { skip: !memberId });
  const [userInfo, setUserInfo] = useState<GetUserResponse | null>(null);

  const [currentPage, setCurrentPage] = useState<CurrentChatPage>(CurrentChatPage.ViewProfile);

  const [memberDialog, setMemberDialog] = useState<DialogT | null>(null);
  const [memberDialogIsLoading, setMemberDialogIsLoading] = useState(false);
  const [memberDialogIsUninitialized, setMemberDialogIsUninitialized] = useState(true);

  const [followUserIsLoading, setFollowUserIsLoading] = useState(false);
  const [unfollowUserIsLoading, setUnfollowUserIsLoading] = useState(false);

  //update avatar url name [concern][reflex]
  useEffect(() => {
    if (!chatSocket?.chatSocket)
      return;

    const updateParticipant = ({ user_id, name, avatar_link } : UpdateUserMsg) => {
      const [firstName, lastName] = name.split(" ");
      setUserInfo(ui => {
        if (ui?.user.unique_id === user_id)
          return {
            ...ui,
            user: {
              ...ui.user,
              avatar_url: avatar_link,
              first_name: firstName,
              last_name: lastName,
            }
          };

        return ui;
      });
    };

    chatSocket.chatSocket.on("userUpdate", updateParticipant);
    return () => {
      chatSocket.chatSocket?.off("userUpdate", updateParticipant);
    };

  }, [chatSocket?.chatSocket]);

  //follow user [state] [concern]s [reflex]
  const [followUserQuery, followUserQueryState] = useLazyFollowUserQuery();

  useEffect(() => {
    if (followUserQueryState.isUninitialized)
      return;

    if (followUserQueryState.isError ||
      followUserQueryState.isFetching)
      return;

    dispatch(incrementFollowingCount());

    userInfo?.user.unique_id &&
    userInfo?.user.username &&
    dispatch(refetchUser({ userId: userInfo?.user.unique_id, username: userInfo?.user.username }));

  }, [followUserQueryState.isFetching]);

  const followMember = useMemo(() => {
    if (!memberId)
      return;

    return () => followUserQuery({ uniqueId: memberId }, false);

  }, [memberId]);

  //unfollow user [state] [concern]s [reflex]
  const [unfollowUserQuery, unfollowUserQueryState] = useLazyUnfollowUserQuery();
  useEffect(() => {
    if (unfollowUserQueryState.isUninitialized)
      return;

    if (unfollowUserQueryState.isError ||
      unfollowUserQueryState.isFetching)
      return;

    dispatch(decrementFollowingCount());

    userInfo?.user.unique_id &&
    userInfo?.user.username &&
    dispatch(refetchUser({ userId: userInfo?.user.unique_id, username: userInfo?.user.username }));

  }, [unfollowUserQueryState.isFetching]);

  const unfollowMember = useMemo(() => {
    if (!memberId)
      return;

    return () => unfollowUserQuery({ uniqueId: memberId }, false);

  }, [memberId]);

  //refetch user info
  useEffect(() => {
    if (!userInfo || userInfo.user.unique_id !== userToRefetch.userId)
      return;

    if (getUserState.isUninitialized)
      return;

    getUserState.refetch();

  }, [userToRefetch]);

  //get dialog [concern]
  const [getDialogByUserId, getDialogByUserIdState] = useLazyGetDialogByUserIdQuery();

  //enter personal dialog [concern]
  const enterPersonalDialog = useCallback(() => {

    setCurrentPage(CurrentChatPage.Dialog);

    getDialogByUserId({ userId: memberId });
    setMemberDialogIsUninitialized(false);
    setMemberDialogIsLoading(true);
  }, [memberId]);

  //user info [reflex]
  useEffect(() => {
    getUserState.currentData && setUserInfo(getUserState.currentData);
  }, [getUserState.currentData]);

  //member dialog [reflex]
  useEffect(() => {
    if (getDialogByUserIdState.isUninitialized)
      return;

    setMemberDialogIsLoading(getDialogByUserIdState.isLoading);

    if (!getDialogByUserIdState.data)
      return;

    const d = getDialogByUserIdState.data;

    const dlm = d.last_message;
    let lastMessage: DialogLastMessage | null = null;
    if (dlm)
      if (dlm.type === MessageType.User)
        lastMessage = {
          type: MessageType.User,
          text: dlm.text,
          username: dlm.username,
        };
      else if (dlm.type === MessageType.System)
        lastMessage = {
          type: MessageType.System,
          systemMessageType: dlm.system_metadata.type,
        };

    const pd: DialogT = {
      type: d.type,
      system: false,
      id: d.chat_id,
      title: d.title,
      avatarUrl: d.avatar_url || avatar_skeleton,
      isVerified: true,
      isRead: true,
      isOnline: true,
      isWarning: dlm?.type === MessageType.System,
      isMute: false,
      lastMessage,
      unreadMessagesCount: d.unread_messages_count,
    };

    setMemberDialog(pd);
    setMemberDialogIsLoading(false);

  }, [getDialogByUserIdState]);

  //follow user is loading [reflex]
  useEffect(() => {
    followUserQueryState.isFetching && setFollowUserIsLoading(true);
    if (followUserQueryState.isError) {
      dispatch(addToast({
        type: "default",
        content: "Something went wrong. Please try later",
        estimate: DEFAULT_NOTIFICATION_ESTIMATE,
      }));
      setFollowUserIsLoading(false);
      return;
    }

  }, [followUserQueryState]);

  useEffect(() => {
    setFollowUserIsLoading(false);

  }, [userInfo]);

  //unfollow user is loading [reflex]

  useEffect(() => {
    unfollowUserQueryState.isFetching && setUnfollowUserIsLoading(true);
    if (unfollowUserQueryState.isError) {
      dispatch(addToast({
        type: "default",
        content: "Something went wrong. Please try later",
        estimate: DEFAULT_NOTIFICATION_ESTIMATE,
      }));
      setUnfollowUserIsLoading(false);
      return;
    }

  }, [unfollowUserQueryState]);

  useEffect(() => {
    setUnfollowUserIsLoading(false);

  }, [userInfo]);

  //
  if (currentPage === CurrentChatPage.Dialog && !memberDialogIsUninitialized)
    if (getUserState.isLoading || memberDialogIsLoading)
      return (
        <div className={styles.spinner}>
          <Spinner size="l" color="grayscale400" />
        </div>);
    else
      return <Dialog
        dialog={memberDialog || void 0}
        dialogType={DialogType.Personal}
        dialogMate={userInfo?.user}
        onBack={() => setCurrentPage(CurrentChatPage.ViewProfile)}
      />;

  if (currentPage !== CurrentChatPage.ViewProfile)
    return null;

  if (getUserState.isLoading)
    return (
      <div className={styles.spinner}>
        <Spinner size="l" color="grayscale400" />
      </div>);

  if (!userInfo)
    return null;

  const getSocialMediaIconLink = (url: string | undefined, Icon: (props: IconProps) => JSX.Element) => {
    if (url) {
      return (
        <Link
          to={url}
          external
          target="_blank"
          hoverUnderLine={false}
        >
          <Icon color="violet600" />
        </Link>);
    }
  };

  const renderContentBackground = (blocks: string[]) => {
    if (blocks.length === 0) {
      return styles.background;
    }
    if (blocks.length === 1) {
      return styles.background_one_field;
    }
    if (blocks.length === 2) {
      return styles.background_two_fields;
    }
    if (blocks.length > 2) {
      return "";
    }
  };

  const {
    user: {
      position,
      company,
      about: description,
    },
  } = userInfo;

  const twitter = userInfo.user.social_links?.find(
    (l) => l.type === SocialMedia.TWITTER
  )?.url;

  const facebook = userInfo.user.social_links?.find(
    (l) => l.type === SocialMedia.FACEBOOK
  )?.url;

  const instagram = userInfo.user.social_links?.find(
    (l) => l.type === SocialMedia.INSTAGRAM
  )?.url;

  const linkedin = userInfo.user.social_links?.find(
    (l) => l.type === SocialMedia.LINKEDIN
  )?.url;

  const linkSite = userInfo.user.social_links?.find(
    (l) => l.type === SocialMedia.WEBSITE
  )?.url;

  const hasUserSocialMedia = twitter || facebook || instagram || linkedin;
  const hasUserJobInfo = position || company;
  const hasUserFullInfo = hasUserJobInfo || hasUserSocialMedia || description || linkSite;

  const getDescriptionBlock = () => (
    <div className={styles.description_wrap}>
      {description && <div className={styles.description_text}>
        <Text size={["s", "s", "s", "s", "m"]}>
          {description}
        </Text>
      </div>
      }
      {
        (hasUserSocialMedia || linkSite) && <div className={styles.social_networks}>
          {
            hasUserSocialMedia && <div className={styles.icons}>
              {twitter && getSocialMediaIconLink(twitter, TwitterIcon)}
              {facebook && getSocialMediaIconLink(facebook, FacebookIcon)}
              {linkedin && getSocialMediaIconLink(linkedin, LinkedinIcon)}
              {instagram && getSocialMediaIconLink(instagram, InstagramIcon)}
            </div>
          }
          {
            linkSite && (
              <div className={styles.link_my_site}>
                <Link color="blue" external target="_blank" to={linkSite}>
                  {formattedUserSiteURL(linkSite)}
                </Link>
              </div>
            )
          }
        </div>
      }
    </div>
  );

  const getFieldsOfFullInfoBlock = () => {
    const fields: string[] = [];
    let jobInfo = "";
    let networksInfo = "";

    if (position && !company) {
      jobInfo += position;
    } else if (!position && company) {
      jobInfo += company;
    } else if (position && company) {
      jobInfo += `${position}, ${company}`;
    }

    if (jobInfo) {
      fields.push(jobInfo);
    }

    if (description) {
      fields.push(description);
    }

    if (hasUserSocialMedia && !linkSite) {
      networksInfo += hasUserSocialMedia;
    } else if (!hasUserSocialMedia && linkSite) {
      networksInfo += linkSite;
    } else if (linkSite && hasUserSocialMedia) {
      networksInfo += `${linkSite} ${hasUserSocialMedia}`;
    }

    if (networksInfo) {
      fields.push(networksInfo);
    }
    return fields;
  };

  return (
    <div className={`${styles.wrap} ${renderContentBackground(getFieldsOfFullInfoBlock())}`}>
      <div className={styles.closeButton} onClick={onClose}>
        <Icons.X size="m" color="violet" />
      </div>
      <div className={`${styles.head_and_info} ${hasUserFullInfo ? "" : styles.mb_16_mobile}`}>
        <div className={styles.head}>
          <img
            className={styles.avatar}
            src={userInfo.user.avatar_url || avatar_skeleton}
            alt=""
          />
          <div className={styles.head_info}>
            <div className={styles.caption}>
              <Text size={["m", "m", "m", "m", "m", "m", 6]}>
                {userInfo.user.first_name}
                {isDesktop ? <br /> : "\u0020" }
                {userInfo.user.last_name}
              </Text>
            </div>
            <div
              className={`${styles.caption} ${isOtherUserProfileEnabled ? styles.username : ""}`}
              onClick={isOtherUserProfileEnabled ? () => {
                navigate(`/profile/${userInfo.user.username}`);
                dispatch(closeChat());
              } : void 0}
            >
              <Text size="s" color="violet600">
                {`@${userInfo.user.username}`}
              </Text>
            </div>
          </div>
        </div>
        {userInfo.privacy === "public" &&
          <div className={styles.information}>
            <div className={styles.info_text}>
              <Text as="h6" color="grayscale900" size="l">
                {userInfo.user.following_count}
              </Text>
              <Text as="p" color="grayscale900" size="s">
                              Following
              </Text>
            </div>
            <div className={styles.info_text}>
              <Text as="h6" color="grayscale900" size="l">
                {userInfo.user.followers_count}
              </Text>
              <Text as="p" color="grayscale900" size="s">
                              Followers
              </Text>
            </div>
          </div>
        }
      </div>
      {userInfo.privacy === "public" &&
        <div className={styles.followButton}>
          {!userInfo.is_following ?
            <Button
              theme="violet"
              type="solid"
              size="s"
              onClick={followMember}
              startIcon={!followUserIsLoading ? "UserPlus" : void 0}
              disabled={followUserIsLoading}
            >{followUserIsLoading ? <Spinner size="m" /> : "Follow"}
            </Button> :
            <Button
              theme="violet"
              type="solid"
              size="s"
              onClick={unfollowMember}
              startIcon={!unfollowUserIsLoading ? "UserX" : void 0}
              disabled={unfollowUserIsLoading}
            >{unfollowUserIsLoading ? <Spinner size="m" /> : "Unfollow"}
            </Button>
          }
        </div>
      }
      <div className={styles.messageButton}>
        {userInfo.is_can_start_chat ?
          <Button
            theme="violet"
            type="flat"
            size="s"
            onClick={enterPersonalDialog}
            startIcon="MessageSquare"
          >Message</Button> :
          <div className={styles.buttonRestricteToMessage}>
            <MessageOff />
            <Text size="s" color="grayscale600">
              {userInfo.user.first_name} has restricted<br />
              messages from other users</Text>
          </div>
        }
      </div>
      {userInfo.privacy === "public" &&
        <div className={`${styles.user_full_info}
          ${hasUserSocialMedia ? "" : styles.start_position_content}
          ${!description ? styles.start_position_content : ""}`}
        >
          {(position || company) && <div className={styles.job_info}>
            <Text size="m" color="grayscale600">
              {position && company ? `${position},` : `${position}`}
            </Text>
            {company && <Text size="m" color="grayscale600">
              {company}
            </Text>}
          </div>
          }
          <div>{getDescriptionBlock()}</div>
        </div>
      }
      {userInfo.privacy === "private" &&
        <CensoredProfileInfo />
      }
    </div>
  );
};

export default ViewProfile;